import React, { useEffect, useLayoutEffect, useRef, lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, Link, useLocation, useNavigationType, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import Home from './pages/Home/Home';
import Footer from './components/Shared/Footer';
import FlashNotice from './components/UI/FlashNotice';
import SiteHeader, { PHONE_WIZARD_ROUTES } from './components/Header/SiteHeader';
import SiteSearch from './components/Search/SiteSearch';
import EmailVerifyReminder from './components/Shared/EmailVerifyReminder';
import GateNotice from './components/Shared/GateNotice';
import ClosurePendingBanner from './components/Shared/ClosurePendingBanner';
import WelcomeBackDigest from './components/Notifications/WelcomeBackDigest';
import NeedsYouTracker from './components/Notifications/NeedsYouTracker';
import AskUniFreelancer from './components/Support/AskUniFreelancer';
import { useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { hasAdminAccess, hasInstructorAccess, hasFacultyAccess } from './utils/roles';
import { NotificationProvider } from './context/NotificationContext';
import RouteSeoDefaults from './seo/RouteSeoDefaults';
import { reportError } from './utils/monitoring';

// ── Academy ───────────────────────────────────────────────────────────────────
const Academy = lazy(() => import('./pages/Academy/Academy'));
const AcademyCatalog = lazy(() => import('./pages/Academy/Courses/AcademyCatalog'));
const CreateContent = lazy(() => import('./pages/Academy/CreateContent/CreateContent'));
const CreateCourse = lazy(() => import('./pages/Academy/Courses/CourseBuilder'));
const InstructorGate = lazy(() => import('./pages/Academy/InstructorGate/InstructorGate'));
const ProjectStudioHome = lazy(() => import('./pages/ProjectStudio/ProjectStudioHome'));
const ProjectStudioBrowse = lazy(() => import('./pages/ProjectStudio/ProjectStudioBrowse'));
const FacultyApply = lazy(() => import('./pages/ProjectStudio/FacultyApply'));
const CourseDetail = lazy(() => import('./pages/Academy/Courses/CourseDetail'));
const CourseLearning = lazy(() => import('./pages/Academy/Courses/CourseLearning'));
const SubmissionResult = lazy(() => import('./pages/Academy/Courses/SubmissionResult'));
const CourseProgress = lazy(() => import('./pages/Academy/Courses/CourseProgress'));
const CreateSeminar = lazy(() => import('./pages/Academy/Seminars/CreateSeminar'));
const SeminarDetails = lazy(() => import('./pages/Academy/Seminars/SeminarDetails'));
const SeminarLaunch = lazy(() => import('./pages/Academy/Seminars/SeminarLaunch'));
const CreateTutorial = lazy(() => import('./pages/Academy/Tutorials/CreateTutorial'));
const TutorialDetail = lazy(() => import('./pages/Academy/Tutorials/TutorialDetail'));
const PodcastDetail = lazy(() => import('./pages/Academy/Podcasts/PodcastDetail'));
const MyLibrary = lazy(() => import('./pages/Academy/MyLibrary'));

// ── Auth & Profile ────────────────────────────────────────────────────────────
const Login = lazy(() => import('./pages/Auth/Login'));
const Signup = lazy(() => import('./pages/Auth/Signup'));
const Username = lazy(() => import('./pages/Username/Username'));
const GetStarted = lazy(() => import('./pages/GetStarted/GetStarted'));
const ClientOnboarding = lazy(() => import('./pages/ClientOnboarding/ClientOnboarding'));
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/Auth/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/Auth/VerifyEmail'));
const VerifyUniversity = lazy(() => import('./pages/Auth/VerifyUniversity'));
const Profile = lazy(() => import('./pages/Profile/Profile'));
const CloseAccount = lazy(() => import('./pages/Profile/CloseAccount'));

// ── Freelancer & Listings ─────────────────────────────────────────────────────
const FindWork = lazy(() => import('./pages/FindWork/FindWork'));
const SellerOnboarding = lazy(() => import('./pages/SellerOnboarding/SellerOnboarding'));
const CreateListing = lazy(() => import('./pages/FindWork/CreateListing'));
const EditListing = lazy(() => import('./pages/FindWork/EditListing'));
const BrowseFreelancers = lazy(() => import('./pages/BrowseFreelancers/BrowseFreelancers'));
const FreelancerProfile = lazy(() => import('./pages/Freelancers/FreelancerProfile'));
const CompanyProfile = lazy(() => import('./pages/Companies/CompanyProfile'));
const HireTalent = lazy(() => import('./pages/HireTalent/HireTalent'));
const SearchResults = lazy(() => import('./pages/Search/SearchResults'));
const ListingDetail = lazy(() => import('./pages/HireTalent/ListingDetail'));
const DirectMessages = lazy(() => import('./pages/Inbox/DirectMessages'));
const Notifications = lazy(() => import('./pages/Notifications/Notifications'));
const Calendar = lazy(() => import('./pages/Calendar/Calendar'));
const MyAgreements = lazy(() => import('./pages/Agreements/MyAgreements'));
const TeamsHome = lazy(() => import('./pages/Teams/TeamsHome'));
const CreateTeam = lazy(() => import('./pages/Teams/CreateTeam'));
const TeamProfile = lazy(() => import('./pages/Teams/TeamProfile'));
const TeamManage = lazy(() => import('./pages/Teams/TeamManage'));
const TeamDashboard = lazy(() => import('./pages/Teams/TeamDashboard'));
const SellerDashboard = lazy(() => import('./pages/SellerDashboard/SellerDashboard'));
const HiringDashboard = lazy(() => import('./pages/HiringDashboard/HiringDashboard'));
const WorkRoom = lazy(() => import('./pages/Work/WorkRoom'));

// ── Instructor & Admin ────────────────────────────────────────────────────────
const InstructorDashboard = lazy(() => import('./pages/Instructor/InstructorDashboard'));
const ProjectStudioManage = lazy(() => import('./pages/Instructor/ProjectStudioManage'));
const ProjectStudioForm = lazy(() => import('./pages/Instructor/ProjectStudioForm'));
const ProjectStudioRoster = lazy(() => import('./pages/Instructor/ProjectStudioRoster'));
// Professor classes (Faculty-verified) — separate from the Academy instructor dashboard.
const ProfessorDashboard = lazy(() => import('./pages/Professor/ProfessorDashboard'));
const ClassPage = lazy(() => import('./pages/Professor/ClassPage'));
const JoinClass = lazy(() => import('./pages/Professor/JoinClass'));
const ClassSetup = lazy(() => import('./pages/Professor/ClassSetup'));
const ClassTemplate = lazy(() => import('./pages/Professor/ClassTemplate'));
const MentorInvite = lazy(() => import('./pages/Professor/MentorInvite'));
const GradingPage = lazy(() => import('./pages/Professor/GradingPage'));
const ProgramStart = lazy(() => import('./pages/Professor/ProgramStart'));
// Dev-only: the Studio pages with sample data (see StudioPreview.jsx).
const StudioPreview = lazy(() => import('./pages/Professor/StudioPreview'));
const CourseManage = lazy(() => import('./pages/Instructor/CourseManage'));
const GradingInterface = lazy(() => import('./pages/Instructor/GradingInterface'));
const ModuleSubmissionReview = lazy(() => import('./pages/Instructor/ModuleSubmissionReview'));
const MentorDashboard = lazy(() => import('./pages/Mentor/MentorDashboard'));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const ProjectStudioDetail = lazy(() => import('./pages/Academy/ProjectStudioDetail'));
const ProjectStudioWork = lazy(() => import('./pages/Academy/ProjectStudioWork'));
const AdminAchievements = lazy(() => import('./pages/Admin/AdminAchievements'));

// ── Payment ───────────────────────────────────────────────────────────────────
const PaymentSuccess = lazy(() => import('./pages/Payment/PaymentSuccess'));

// ── Social & About ────────────────────────────────────────────────────────────
const Social = lazy(() => import('./pages/Social/Social'));
const UniCoursePage = lazy(() => import('./pages/Social/UniCoursePage'));
const CourseDiscussionBoard = lazy(() => import('./pages/Social/CourseDiscussionBoard'));
const ActivityThread = lazy(() => import('./pages/Social/ActivityThread'));
const PostDetail = lazy(() => import('./pages/Social/PostDetail'));
const About = lazy(() => import('./pages/About/About'));
const HowItWorks = lazy(() => import('./pages/About/HowItWorks'));
const CampusPage = lazy(() => import('./pages/Campus/CampusPage'));
const CertificatePage = lazy(() => import('./pages/Academy/Certificate/CertificatePage'));
const Terms = lazy(() => import('./pages/Legal/Terms'));
const Privacy = lazy(() => import('./pages/Legal/Privacy'));
const PaymentsAndTaxes = lazy(() => import('./pages/Legal/PaymentsAndTaxes'));
const HelpCenter = lazy(() => import('./pages/Help/HelpCenter'));
const HelpCategory = lazy(() => import('./pages/Help/HelpCategory'));
const HelpArticle = lazy(() => import('./pages/Help/HelpArticle'));

// ── Job Postings ──────────────────────────────────────────────────────────────
const JobPostings = lazy(() => import('./pages/JobPostings/JobsPostings'));
const CreateJobPosting = lazy(() => import('./pages/JobPostings/CreateJobPosting'));
const JobPostingDetail = lazy(() => import('./pages/JobPostings/JobPostingDetail'));
const EditJobPosting = lazy(() => import('./pages/JobPostings/EditJobPosting'));
const ViewApplicants = lazy(() => import('./pages/JobPostings/ViewApplicants'));
const PostAJob = lazy(() => import('./pages/PostAJob/PostAJob'));
const Saved = lazy(() => import('./pages/Saved/Saved'));
const LtiBridge = lazy(() => import('./pages/Lti/LtiBridge'));
const LtiStudentView = lazy(() => import('./pages/Lti/LtiStudentView'));

// ─────────────────────────────────────────────────────────────────────────────
// ScrollToTop — resets scroll on route change except between hub tabs
// ─────────────────────────────────────────────────────────────────────────────
// Scroll position per history entry, so Back/Forward can return you to
// where you were. Module-level: it outlives any one page's render.
const savedScroll = new Map();
let restoreFrame = 0;
function stopRestore() {
  cancelAnimationFrame(restoreFrame);
  restoreFrame = 0;
}

function ScrollToTop() {
  const { pathname, key } = useLocation();
  const navigationType = useNavigationType();
  const previousPathnameRef = useRef(pathname);
  const keyRef = useRef(key);
  keyRef.current = key;

  // Recorded while scrolling, not on leaving: by the time the next page has
  // rendered, a shorter page has already clamped scrollY.
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        savedScroll.set(keyRef.current, window.scrollY);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    // The person scrolling themselves always wins over a restore in progress.
    window.addEventListener('wheel', stopRestore, { passive: true });
    window.addEventListener('touchstart', stopRestore, { passive: true });
    window.addEventListener('keydown', stopRestore);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('wheel', stopRestore);
      window.removeEventListener('touchstart', stopRestore);
      window.removeEventListener('keydown', stopRestore);
      cancelAnimationFrame(frame);
    };
  }, []);

  // The browser's own back/forward scroll memory races this component's
  // reset — it can restore a few pixels of scroll before React re-renders,
  // which is enough to snap the sticky nav row (top: 0, see the bottom-row
  // header below) up over the logo/search bar above it. Owning scroll
  // restoration entirely here (instead of letting the browser also try)
  // removes that race.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useLayoutEffect(() => {
    const prev = previousPathnameRef.current;
    previousPathnameRef.current = pathname;

    // Back/Forward: return to the saved position. Most pages load their
    // content after mounting, so keep retrying until the page is tall enough
    // (or ~2s pass), then settle wherever it got to.
    // A page rewriting its own URL (REPLACE, e.g. Hire Talent saving its
    // filters) is the same page: leave scroll, and any restore, alone.
    if (navigationType === 'REPLACE' && prev === pathname) return;
    stopRestore();

    const target = savedScroll.get(key);
    if (navigationType === 'POP' && target > 0) {
      const started = performance.now();
      const attempt = () => {
        window.scrollTo(0, target);
        restoreFrame = Math.abs(window.scrollY - target) > 2 && performance.now() - started < 2000
          ? requestAnimationFrame(attempt)
          : 0;
      };
      attempt();
      return;
    }

    if (prev === pathname) return;
    const hubTabs = ['/academy/courses', '/academy/seminars', '/academy/tutorials', '/academy/podcasts'];
    if (!(hubTabs.includes(pathname) && hubTabs.includes(prev))) {
      window.scrollTo(0, 0);
    }
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// PageFallback — shown while a lazily-loaded route chunk is fetching
// ─────────────────────────────────────────────────────────────────────────────
// Shown inside the normal header/footer for any route that doesn't exist.
// Places a lost visitor most likely meant to reach.
const NOT_FOUND_LINKS = [
  { to: '/hire-talent', label: 'Browse services', sub: 'Hire for a set price' },
  { to: '/job-postings', label: 'Browse jobs', sub: 'Find open work' },
  { to: '/academy', label: 'Academy', sub: 'Courses and seminars' },
  { to: '/help', label: 'Help Center', sub: 'Answers and support' },
];

function NotFound({ user }) {
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center gap-4 px-4 py-12 text-center font-academy">
      <p className="text-[13px] font-semibold text-press-muted">Page not found</p>
      <h1 className="text-balance font-press-display text-[28px] font-extrabold leading-tight text-press-ink">This link doesn&apos;t lead anywhere</h1>
      <p className="max-w-[40ch] text-[15px] text-press-muted">It may be old, or the page moved. Search for what you were after, or start from one of these.</p>
      <div className="w-full text-left"><SiteSearch user={user} /></div>
      <ul className="m-0 grid w-full list-none grid-cols-2 gap-2 p-0">
        {NOT_FOUND_LINKS.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="block h-full rounded-xl border border-press-border px-3 py-2.5 text-left transition hover:border-press-ink">
              <span className="block text-[14px] font-semibold text-press-ink">{l.label}</span>
              <span className="block text-[12px] text-press-muted">{l.sub}</span>
            </Link>
          </li>
        ))}
      </ul>
      {user && <Link to="/dashboard" className="text-[13px] font-semibold text-press-brass-ink hover:underline">Go to your dashboard</Link>}
    </div>
  );
}
NotFound.propTypes = { user: PropTypes.object };

// A render error (or a stale lazy chunk after a deploy, "ChunkLoadError")
// used to blank the whole page. This keeps the header/footer and offers a
// reload. Keyed by path so moving to another page clears the error.
class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.error('Page crashed:', error);
    reportError(error);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div role="alert" className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center gap-3 px-4 text-center font-academy">
        <h1 className="text-balance font-press-display text-[26px] font-extrabold leading-tight text-press-ink">Something broke on this page</h1>
        <p className="text-[15px] text-press-muted">Reloading usually fixes it. If it keeps happening, email support@unifreelancer.com.</p>
        <div className="mt-3 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-full bg-press-ink px-5 py-2.5 text-sm font-semibold text-press-paper transition hover:bg-press-ink/90"
          >
            Reload page
          </button>
          <button
            type="button"
            onClick={() => window.history.back()}
            className="rounded-full border border-press-rule px-5 py-2.5 text-sm font-semibold text-press-ink transition hover:border-press-ink"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }
}
PageErrorBoundary.propTypes = { children: PropTypes.node };

function RouteErrorBoundary({ children }) {
  const { pathname } = useLocation();
  return <PageErrorBoundary key={pathname}>{children}</PageErrorBoundary>;
}
RouteErrorBoundary.propTypes = { children: PropTypes.node };

function PageFallback() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div
        aria-label="Loading…"
        style={{
          width: 34, height: 34, borderRadius: '50%',
          border: '3px solid #e8e4df', borderTopColor: '#E8602C',
          animation: 'uf-spin 0.7s linear infinite',
        }}
      />
      <style>{'@keyframes uf-spin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );
}

// /inbox/messages is the legacy path; the inbox lives at /inbox now. The
// query string has to survive the hop — ?with= is what picks the conversation
// to open, and ?listing=/?order= attach context to it. A bare
// <Navigate to="/inbox"> drops all of it and lands on the empty
// "Select a conversation" state instead of the thread you asked for.
function RedirectToInbox() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/inbox', search }} replace />;
}

// Study-table notifications sent before the course hub linked to
// /social/tables/:courseId; the course page lives at /social/courses/ now.
function RedirectToUniCourse() {
  const { courseId } = useParams();
  return <Navigate to={`/social/courses/${courseId}`} replace />;
}

// Mirrors DashboardRedirect's precedence (admin > defaultView) for the "/"
// route: unauthenticated or still-loading visitors and admin accounts keep
// their current behavior. A signed-in member account — including an
// instructor whose defaultView isn't 'instructor' — goes straight to their
// dashboard instead of a separate member homepage; landing on "/" while
// logged in was never meant to be a destination of its own.
function HomeRouter({ user, authLoading }) {
  if (authLoading || !user) return <Home />;
  if (user.accountType !== 'member' && hasAdminAccess(user)) return <Home />;
  if (user.defaultView === 'instructor' && hasInstructorAccess(user)) return <Home />;
  return <Navigate to={user.defaultView === 'freelancing' ? '/seller/dashboard' : '/hiring/dashboard'} replace />;
}

// "Your dashboard" had no address — only /seller/dashboard and /hiring/dashboard
// existed, so anything wanting to send someone to their own dashboard had to
// know their mode first. /dashboard is that stable address; it resolves to
// whichever side the account is currently working.
function DashboardRedirect() {
  const { user, authLoading } = useAuth();
  const location = useLocation();
  if (authLoading) return <div role="status" style={{ padding: '2rem' }}>Loading…</div>;
  if (!user) return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (user.accountType !== 'member' && hasAdminAccess(user)) return <Navigate to="/admin" replace />;
  // An account with instructor access lands on Teaching only when that’s
  // their chosen defaultView — someone who’s both an instructor and an
  // active freelancer/hirer otherwise keeps landing on whichever of those
  // they picked, same as before they were ever approved to teach.
  // A verified professor's teaching home is the Faculty dashboard, not the
  // Academy instructor one (they're separate on purpose).
  if (user.defaultView === 'instructor' && hasFacultyAccess(user)) return <Navigate to="/professor" replace />;
  if (user.defaultView === 'instructor' && hasInstructorAccess(user)) return <Navigate to="/instructor/dashboard" replace />;
  return <Navigate to={user.defaultView === 'freelancing' ? '/seller/dashboard' : '/hiring/dashboard'} replace />;
}

// Old standalone routes (My Services/Job Postings, Orders, Contracts, Browse
// Freelancers) folded into the Selling/Hiring dashboards as tabs — this
// redirects a bookmarked/emailed old URL to its new `?section=` home while
// carrying forward whatever other query params it already had (e.g. `?tab=`,
// `?order=`, `?paid=`), which the embedded page itself still reads.
function OldRouteRedirect({ to, section }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('section', section);
  return <Navigate to={`${to}?${params.toString()}`} replace />;
}

// /orders and /my-contracts don't always mean the same dashboard — which one
// depends on the account's defaultView, unlike the other three old routes
// which only ever mean one dashboard.
// An explicit `?tab=` names the side, and each side now lives on exactly one
// dashboard (the embedded lists are locked to it), so it beats defaultView.
// An `?order=` link with no tab still guesses; Orders hops to the other
// dashboard itself if the order turns out to be on the other side.
const SELLING_TABS = ['selling', 'freelancing'];
const BUYING_TABS = ['buying', 'hiring'];

function useSideDashboard() {
  const { user } = useAuth();
  const tab = new URLSearchParams(useLocation().search).get('tab');
  if (SELLING_TABS.includes(tab)) return '/seller/dashboard';
  if (BUYING_TABS.includes(tab)) return '/hiring/dashboard';
  return user?.defaultView === 'freelancing' ? '/seller/dashboard' : '/hiring/dashboard';
}

function OrdersRedirect() {
  const { authLoading } = useAuth();
  const to = useSideDashboard();
  if (authLoading) return <div role="status" style={{ padding: '2rem' }}>Loading…</div>;
  return <OldRouteRedirect to={to} section="work" />;
}

function ContractsRedirect() {
  const { authLoading } = useAuth();
  const to = useSideDashboard();
  if (authLoading) return <div role="status" style={{ padding: '2rem' }}>Loading…</div>;
  return <OldRouteRedirect to={to} section="work" />;
}

// Canvas already wraps an LTI launch in its own page chrome, so a second nav
// bar inside the iframe would just be redundant and cramped. Every /lti/*
// route renders bare.
function ConditionalHeader({ user }) {
  const location = useLocation();
  if (location.pathname.startsWith('/lti/')) return null;
  return <SiteHeader user={user} />;
}

// Same reasoning as ConditionalHeader — the marketing footer under a view
// that's already inside Canvas's own page is noise.
function ConditionalFooter() {
  const location = useLocation();
  if (location.pathname.startsWith('/lti/')) return null;
  if (PHONE_WIZARD_ROUTES.test(location.pathname)) return <div className="phone:hidden"><Footer /></div>;
  return <Footer />;
}

// ─────────────────────────────────────────────────────────────────────────────
// AppShell — manages auth state and renders layout
// ─────────────────────────────────────────────────────────────────────────────
function AppShell() {
  const { user, authLoading } = useAuth();
  return (
    <>
      <SocketProvider user={user}>
      <NotificationProvider user={user}>
      <RouteSeoDefaults />
      <ScrollToTop />
      <FlashNotice />
      <div className="min-h-screen flex flex-col">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[200] focus:rounded-full focus:bg-press-ink focus:px-5 focus:py-2.5 focus:text-sm focus:font-semibold focus:text-press-paper focus:outline-none focus:ring-2 focus:ring-press-ink/15"
        >
          Skip to main content
        </a>
        <ConditionalHeader user={user} />
        <ClosurePendingBanner user={user} />
        <EmailVerifyReminder user={user} />
        {user && <GateNotice />}
        {user && <WelcomeBackDigest user={user} />}
        {/* On phones the tab bar's Work count stands in for it. */}
        {user && <div className="phone:hidden"><NeedsYouTracker user={user} /></div>}
        {user && <AskUniFreelancer user={user} />}

        <main id="main-content" tabIndex={-1} className="flex-1 focus:outline-none">
          <RouteErrorBoundary>
          <Suspense fallback={<PageFallback />}>
          <Routes>

            {/* ── Site search (where Enter in the topbar lands) ── */}
            <Route path="/search" element={<SearchResults />} />

            {/* ── Home ────────────────────────────────────────────────────── */}
            <Route path="/" element={<HomeRouter user={user} authLoading={authLoading} />} />
            {/* Always the real Home page, logged in or not — see the nav's
                "Home" link comment above for why this needs to be distinct
                from '/'. */}
            <Route path="/home" element={<Home />} />

            {/* ── Academy ─────────────────────────────────────────────────── */}
            <Route path="/academy" element={<Academy />} />
            <Route path="/academy/courses" element={<AcademyCatalog />} />
            <Route path="/academy/seminars" element={<AcademyCatalog />} />
            <Route path="/academy/tutorials" element={<AcademyCatalog />} />
            <Route path="/academy/podcasts" element={<AcademyCatalog />} />
            {/* Old catalog-tab URL — Project Studio now has its own standalone
                page (see /project-studio above). Redirect, don't 404, for any
                bookmarks/external links to the old path. */}
            <Route path="/academy/project-studio" element={<Navigate to="/project-studio" replace />} />
            <Route path="/academy/my-courses" element={<Navigate to="/academy/my-library" replace />} />
            <Route path="/academy/create" element={
              <RequireAuth user={user} authLoading={authLoading}>
                <CreateContent user={user} />
              </RequireAuth>
            } />
            {/* Non-instructors land on the application/pitch gate instead of
                the builder — see InstructorGate.jsx. It renders CreateCourse
                itself once access is confirmed (existing instructor, or an
                approved-and-unconsumed course pitch), so this stays a single
                conditional rather than a second route. */}
            <Route path="/academy/create/course" element={
              <RequireAuth user={user} authLoading={authLoading}>
                {hasInstructorAccess(user) ? <CreateCourse /> : <InstructorGate user={user} />}
              </RequireAuth>
            } />
            <Route path="/academy/create/:courseId" element={
              <RequireAuth user={user} authLoading={authLoading}>
                <CreateCourse />
              </RequireAuth>
            } />
            {/* Same gate as courses: the API refuses non-instructors, and a
                student used to fill in all four steps before hearing so. */}
            <Route path="/academy/create/seminar" element={
              <RequireAuth user={user} authLoading={authLoading}>
                {hasInstructorAccess(user) ? <CreateSeminar /> : <InstructorGate user={user} />}
              </RequireAuth>
            } />
            <Route path="/academy/create/project-studio" element={
              <InstructorRoute user={user} authLoading={authLoading}>
                <ProjectStudioForm />
              </InstructorRoute>
            } />
            <Route path="/academy/create/project-studio/:id" element={
              <InstructorRoute user={user} authLoading={authLoading}>
                <ProjectStudioForm />
              </InstructorRoute>
            } />
            <Route path="/academy/courses/:id" element={<CourseDetail />} />
            <Route path="/academy/courses/:id/learn" element={<CourseLearning />} />
            <Route path="/academy/submissions/:submissionId" element={<SubmissionResult />} />
            <Route path="/academy/courses/:id/progress" element={<CourseProgress />} />
            <Route path="/academy/seminars/:id" element={<SeminarDetails />} />
            <Route path="/academy/seminars/:id/join" element={<RequireAuth user={user} authLoading={authLoading}><SeminarLaunch /></RequireAuth>} />
            <Route path="/academy/tutorials/:id" element={<TutorialDetail />} />
            <Route path="/academy/podcasts/:id" element={<PodcastDetail />} />
            <Route path="/academy/project-studio/:id" element={<ProjectStudioDetail />} />
            <Route path="/academy/project-studio/:id/work" element={<ProjectStudioWork />} />
            <Route path="/academy/my-library" element={<RequireAuth user={user} authLoading={authLoading}><MyLibrary /></RequireAuth>} />
            <Route path="/academy/payment-success" element={<PaymentSuccess />} />

            {/* ── Academy — Admin-only ─────────────────────────────────────── */}
            <Route path="/academy/create/tutorial" element={
              <AdminRoute user={user} authLoading={authLoading}>
                <CreateTutorial />
              </AdminRoute>
            } />
            <Route path="/academy/tutorials/:id/edit" element={
              <AdminRoute user={user} authLoading={authLoading}>
                <CreateTutorial />
              </AdminRoute>
            } />

            {/* ── Auth & Profile ───────────────────────────────────────────── */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/username" element={<RequireAuth user={user} authLoading={authLoading}><Username /></RequireAuth>} />
            <Route path="/get-started" element={<RequireAuth user={user} authLoading={authLoading}><GetStarted /></RequireAuth>} />
            <Route path="/lti/bridge" element={<LtiBridge />} />
            <Route path="/lti/student" element={<LtiStudentView />} />
            <Route path="/client-onboarding" element={<RequireAuth user={user} authLoading={authLoading}><ClientOnboarding /></RequireAuth>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/verify-university" element={<VerifyUniversity />} />
            <Route path="/profile" element={<RequireAuth user={user} authLoading={authLoading}><Profile /></RequireAuth>} />
            <Route path="/settings" element={<RequireAuth user={user} authLoading={authLoading}><Profile /></RequireAuth>} />
            <Route path="/profile/close" element={<RequireAuth user={user} authLoading={authLoading}><CloseAccount /></RequireAuth>} />

            {/* ── Freelancer Marketplace ───────────────────────────────────── */}
            <Route path="/my-listings" element={<OldRouteRedirect to="/seller/dashboard" section="services" />} />
            <Route path="/my-listings/listing/:id" element={<ListingDetail user={user} />} />
            <Route path="/browse-freelancers" element={<BrowseFreelancers />} />
            <Route path="/freelancer/:id" element={<FreelancerProfile />} />
            <Route path="/company/:id" element={<CompanyProfile />} />
            <Route path="/hire-talent" element={<HireTalent user={user} />} />
            <Route path="/hire-talent/listing/:id" element={<ListingDetail user={user} />} />
            <Route path="/dashboard" element={<DashboardRedirect />} />
            <Route path="/seller/dashboard" element={<RequireAuth user={user} authLoading={authLoading}><SellerDashboard /></RequireAuth>} />

            {/* ── Inbox ────────────────────────────────────────────────────── */}
            <Route path="/work/:kind/:id" element={<RequireAuth user={user} authLoading={authLoading}><WorkRoom /></RequireAuth>} />
            {/* The Work docket lives in each dashboard; notifications and the
                close-account checklist link here. */}
            <Route path="/work" element={<OrdersRedirect />} />
            {/* Old links already sitting in people's notifications and emails. */}
            <Route path="/profile/security" element={<Navigate to="/settings?section=security" replace />} />
            <Route path="/profile/payouts" element={<Navigate to="/settings?section=payouts" replace />} />
            <Route path="/instructor/courses" element={<Navigate to="/instructor/dashboard" replace />} />
            <Route path="/studio" element={<Navigate to="/professor" replace />} />
            <Route path="/my-listings/:id/requests" element={<Navigate to="/seller/dashboard?section=services" replace />} />
            <Route path="/orders" element={<OrdersRedirect />} />
            <Route path="/my-contracts" element={<ContractsRedirect />} />
            <Route path="/calendar" element={<RequireAuth user={user} authLoading={authLoading}><Calendar /></RequireAuth>} />
            <Route path="/my-agreements" element={<RequireAuth user={user} authLoading={authLoading}><MyAgreements /></RequireAuth>} />
            <Route path="/inbox" element={<RequireAuth user={user} authLoading={authLoading}><DirectMessages user={user} authLoading={authLoading} /></RequireAuth>} />
            <Route path="/teams" element={<TeamsHome />} />

            {/* ── Project Studio — standalone page, split out of Academy ──────
                /academy/project-studio (the old catalog tab's route) redirects
                here below; /academy/project-studio/:id (a specific project) and
                /academy/project-studio/manage* (the faculty dashboard) are
                unrelated and stay exactly where they are. */}
            <Route path="/project-studio" element={<ProjectStudioHome user={user} />} />
            <Route path="/project-studio/browse" element={
              <RequireAuth user={user} authLoading={authLoading}>
                <ProjectStudioBrowse user={user} />
              </RequireAuth>
            } />
            <Route path="/project-studio/apply" element={
              <RequireAuth user={user} authLoading={authLoading}>
                <FacultyApply user={user} />
              </RequireAuth>
            } />

            <Route path="/teams/new" element={<RequireAuth user={user} authLoading={authLoading}><CreateTeam /></RequireAuth>} />
            <Route path="/teams/:slug/manage" element={<RequireAuth user={user} authLoading={authLoading}><TeamManage /></RequireAuth>} />
            <Route path="/teams/:slug/dashboard" element={<RequireAuth user={user} authLoading={authLoading}><TeamDashboard /></RequireAuth>} />
            <Route path="/teams/:slug" element={<TeamProfile />} />
            <Route path="/notifications" element={<RequireAuth user={user} authLoading={authLoading}><Notifications /></RequireAuth>} />
            <Route path="/inbox/messages" element={<RedirectToInbox />} />
            <Route path="/inbox/bookings" element={<Navigate to="/seller/dashboard?section=services" replace />} />

            {/* ── Instructor ───────────────────────────────────────────────── */}
            <Route path="/instructor/dashboard" element={<RequireAuth user={user} authLoading={authLoading}><InstructorDashboard /></RequireAuth>} />
            <Route path="/academy/project-studio/manage" element={
              <InstructorRoute user={user} authLoading={authLoading}>
                <ProjectStudioManage />
              </InstructorRoute>
            } />
            <Route path="/academy/project-studio/manage/:id/roster" element={
              <RequireAuth user={user} authLoading={authLoading}>
                <ProjectStudioRoster />
              </RequireAuth>
            } />

            {/* ── Professor classes ────────────────────────────────────────── */}
            <Route path="/professor" element={
              <FacultyRoute user={user} authLoading={authLoading}>
                <ProfessorDashboard />
              </FacultyRoute>
            } />
            <Route path="/professor/new" element={
              <FacultyRoute user={user} authLoading={authLoading}>
                <ProgramStart />
              </FacultyRoute>
            } />
            <Route path="/classes/:id" element={
              <RequireAuth user={user} authLoading={authLoading}>
                <ClassPage />
              </RequireAuth>
            } />
            <Route path="/classes/:id/template" element={
              <FacultyRoute user={user} authLoading={authLoading}>
                <ClassTemplate />
              </FacultyRoute>
            } />
            <Route path="/classes/:id/setup" element={
              <FacultyRoute user={user} authLoading={authLoading}>
                <ClassSetup />
              </FacultyRoute>
            } />
            {/* Public like /join: an invited mentor is usually signed out. */}
            <Route path="/mentor-invite/:token" element={<MentorInvite />} />
            <Route path="/classes/:id/grade" element={
              <RequireAuth user={user} authLoading={authLoading}>
                <GradingPage />
              </RequireAuth>
            } />
            {/* Public on purpose: a student following an invite link is
                usually signed out. JoinClass previews the class first, then
                sends them to log in or sign up and back (returnTo). */}
            <Route path="/join" element={<JoinClass />} />
            <Route path="/join/:code" element={<JoinClass />} />
            {process.env.NODE_ENV === 'development' && (
              <Route path="/studio-preview" element={<StudioPreview />} />
            )}
            <Route path="/instructor/courses/:courseId" element={<RequireAuth user={user} authLoading={authLoading}><CourseManage /></RequireAuth>} />
            <Route path="/instructor/grade/:submissionId" element={<RequireAuth user={user} authLoading={authLoading}><GradingInterface /></RequireAuth>} />
            <Route path="/instructor/module-submission/:id" element={<RequireAuth user={user} authLoading={authLoading}><ModuleSubmissionReview /></RequireAuth>} />

            {/* ── Mentor ────────────────────────────────────────────────────── */}
            <Route path="/mentor/dashboard" element={<RequireAuth user={user} authLoading={authLoading}><MentorDashboard /></RequireAuth>} />

            {/* ── Admin ────────────────────────────────────────────────────── */}
            <Route path="/admin" element={
              <AdminRoute user={user} authLoading={authLoading}>
                <AdminDashboard />
              </AdminRoute>
            } />
            <Route path="/admin/achievements" element={
              <AdminRoute user={user} authLoading={authLoading}>
                <AdminAchievements />
              </AdminRoute>
            } />

            {/* ── Social & About ───────────────────────────────────────────── */}
            <Route path="/social" element={<Social tab="courses" />} />
            <Route path="/social/board" element={<Social tab="board" />} />
            <Route path="/social/community" element={<Social tab="community" />} />
            <Route path="/social/courses/:courseId" element={<UniCoursePage />} />
            <Route path="/social/tables" element={<Navigate to="/social" replace />} />
            <Route path="/social/tables/:courseId" element={<RedirectToUniCourse />} />
            <Route path="/social/course-board/:courseId" element={<CourseDiscussionBoard />} />
            <Route path="/social/activity/:courseId/:moduleId/:activityKey" element={<ActivityThread />} />
            <Route path="/social/board/:postId" element={<PostDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/hire/:category/:school" element={<CampusPage />} />
            <Route path="/certificates/:code" element={<CertificatePage />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/payments-and-taxes" element={<PaymentsAndTaxes />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/help/:categorySlug" element={<HelpCategory />} />
            <Route path="/help/:categorySlug/:articleSlug" element={<HelpArticle />} />

            {/* ── Job Postings ─────────────────────────────────────────────── */}
            <Route path="/find-work" element={<FindWork />} />
            <Route path="/seller-onboarding" element={<RequireAuth user={user} authLoading={authLoading}><SellerOnboarding /></RequireAuth>} />
            <Route path="/find-work/create-listing" element={<RequireAuth user={user} authLoading={authLoading}><CreateListing /></RequireAuth>} />
            <Route path="/find-work/edit-listing/:id" element={<RequireAuth user={user} authLoading={authLoading}><EditListing user={user} /></RequireAuth>} />
            <Route path="/post-a-job" element={<PostAJob />} />
            <Route path="/hiring/dashboard" element={<RequireAuth user={user} authLoading={authLoading}><HiringDashboard /></RequireAuth>} />
            <Route path="/job-postings" element={<JobPostings />} />
            <Route path="/job-postings/create" element={<RequireAuth user={user} authLoading={authLoading}><CreateJobPosting user={user} /></RequireAuth>} />
            <Route path="/my-job-postings" element={<OldRouteRedirect to="/hiring/dashboard" section="postings" />} />
            <Route path="/saved" element={<RequireAuth user={user} authLoading={authLoading}><Saved /></RequireAuth>} />
            <Route path="/my-proposals" element={<OldRouteRedirect to="/seller/dashboard" section="proposals" />} />
            <Route path="/job-postings/edit/:id" element={<RequireAuth user={user} authLoading={authLoading}><EditJobPosting user={user} /></RequireAuth>} />
            {/* Guarded by RequireAuth rather than its own !user check: this is
                the page notification emails link to, so it is routinely opened
                cold. A page-level redirect fires while auth is still resolving
                (user is null before /me answers) and bounced a logged-in client
                straight to the login screen. RequireAuth waits on authLoading. */}
            <Route path="/job-postings/:id/applicants" element={
              <RequireAuth user={user} authLoading={authLoading}>
                <ViewApplicants />
              </RequireAuth>
            } />
            <Route path="/job-postings/:id" element={<JobPostingDetail user={user} />} />

            {/* Anything unmatched (typo, old bookmark, removed page). Without
                this the shell rendered around an empty <main>. */}
            <Route path="*" element={<NotFound user={user} />} />
          </Routes>
          </Suspense>
          </RouteErrorBoundary>
        </main>

        <ConditionalFooter />
      </div>
      </NotificationProvider>
      </SocketProvider>
    </>
  );
}

// A data router with one catch-all route, so the existing <Routes> tree works
// unchanged while hooks that need a data router (useBlocker, which lets
// useUnsavedChangesGuard catch the browser Back button) are available
// everywhere. Created once at module scope, as React Router requires.
const router = createBrowserRouter([{ path: '*', element: <AppShell /> }]);

function App() {
  return <RouterProvider router={router} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// AdminRoute — protects routes that require admin account type
// ─────────────────────────────────────────────────────────────────────────────
function AdminRoute({ user, authLoading, children }) {
  const location = useLocation();
  if (authLoading) return <div role="status" style={{ padding: '2rem' }}>Loading…</div>;
  if (!user) return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (!hasAdminAccess(user)) return <Navigate to="/academy/tutorials" replace />;
  return children;
}

// ─────────────────────────────────────────────────────────────────────────────
// RequireAuth — protects routes that require any logged-in account (no role
// check). Used by the Academy content-creation flow so a signed-out visitor
// is sent to log in instead of being able to start filling out a course,
// webinar, or project studio form.
// ─────────────────────────────────────────────────────────────────────────────
function RequireAuth({ user, authLoading, children }) {
  const location = useLocation();
  if (authLoading) return <div role="status" style={{ padding: '2rem' }}>Loading…</div>;
  if (!user) return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return children;
}

// ─────────────────────────────────────────────────────────────────────────────
// InstructorRoute — protects Project Studio authoring routes. Unlike
// /academy/create/course, Project Studio has no pitch/application path for
// non-instructors (InstructorGate.jsx is course-specific), so a non-instructor
// is bounced to the create hub instead of shown a gate of their own. Backend
// (studioProjects.js) already enforces this with requireInstructor + an
// ownership check, but leaving the client route open to any logged-in user
// meant the create form and the "Manage" dashboard were both reachable —
// and the dashboard's own project list didn't filter to the owner, so it
// showed every instructor's published projects with Edit/Delete controls
// that only failed once clicked (see ProjectStudioManage.jsx's `mine=true`).
// ─────────────────────────────────────────────────────────────────────────────
function InstructorRoute({ user, authLoading, children }) {
  const location = useLocation();
  if (authLoading) return <div role="status" style={{ padding: '2rem' }}>Loading…</div>;
  if (!user) return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (!hasInstructorAccess(user)) return <Navigate to="/academy/create" replace />;
  return children;
}

// ─────────────────────────────────────────────────────────────────────────────
// FacultyRoute — the professor class dashboard. Stricter than InstructorRoute:
// any approved Academy instructor can teach a course, but running a university
// class needs an admin-verified Faculty account. Someone who isn't yet is sent
// to the Faculty apply page (see pages/ProjectStudio/FacultyApply.jsx). The
// API enforces the same rule (routes/studioClasses.js) — this just avoids a
// dead page.
// ─────────────────────────────────────────────────────────────────────────────
function FacultyRoute({ user, authLoading, children }) {
  const location = useLocation();
  if (authLoading) return <div role="status" style={{ padding: '2rem' }}>Loading…</div>;
  if (!user) return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (!hasFacultyAccess(user)) return <Navigate to="/project-studio/apply" replace />;
  return children;
}

AdminRoute.propTypes = {
  user: PropTypes.shape({ accountType: PropTypes.string }),
  authLoading: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

RequireAuth.propTypes = {
  user: PropTypes.shape({ accountType: PropTypes.string }),
  authLoading: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

InstructorRoute.propTypes = {
  user: PropTypes.shape({ accountType: PropTypes.string }),
  authLoading: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

export default App;